import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";

// Handles the return from Emergent-managed Google Auth. The session_id arrives
// in the URL fragment; we exchange it for our unified fm_token session, then
// send the shopper back to wherever they started (plan/search preserved).
export default function AuthCallback() {
  const { exchangeGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    const returnTo = sessionStorage.getItem("fm_auth_return") || "/account";

    (async () => {
      if (!sessionId) {
        navigate("/account", { replace: true });
        return;
      }
      const res = await exchangeGoogle(sessionId);
      // Strip the session_id from the URL so it can't be reused/bookmarked.
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      sessionStorage.removeItem("fm_auth_return");
      // Fitters with no explicit return path land on their portal.
      const dest = !res.ok
        ? "/account?error=google"
        : returnTo === "/account" && res.user?.role === "fitter"
        ? "/fitter/dashboard"
        : returnTo;
      navigate(dest, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="auth-callback">
      <div className="text-center">
        <BrandMark className="h-14 w-14 mx-auto" />
        <div className="mt-4 inline-flex items-center gap-2 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin text-[#E05915]" /> Signing you in…
        </div>
      </div>
    </div>
  );
}
